import type { ReactNode } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkOverlay, { OVERLAY_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkSkeleton, {
  IMAGE_SKELETON_STYLE,
  SKELETON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-skeleton';
import { useImagePreloader } from '@web-platform/shared-universal-utils-frontend/src/helpers/hooks/useImagePreloader';
import classNames from 'classnames';

import STYLES from './SponsoredHeroBanner.module.scss';

type SponsoredHeroBannerProps = {
  imageUrl: string;
  children: ReactNode;
  overlayType?: (typeof OVERLAY_TYPES)[keyof typeof OVERLAY_TYPES];
  mobileOverlayType?: (typeof OVERLAY_TYPES)[keyof typeof OVERLAY_TYPES];
  fallbackBackgroundColor?: string;
  containerClassName?: string;
  testId?: string;
};

const SponsoredHeroBanner = ({
  children,
  containerClassName,
  fallbackBackgroundColor,
  imageUrl,
  mobileOverlayType = OVERLAY_TYPES.solidMedium,
  overlayType = OVERLAY_TYPES.leftHigh,
  testId,
}: SponsoredHeroBannerProps) => {
  const isMobile = !!useMediaQuery(BREAKPOINTS.MOBILE);
  const imageLoaded = useImagePreloader(imageUrl);

  return (
    <div
      className={classNames(STYLES.container, containerClassName)}
      data-testid={imageLoaded ? testId : undefined}
    >
      {imageLoaded ? (
        <>
          <img src={imageUrl} alt="" aria-hidden className={STYLES.heroImage} />
          <BpkOverlay
            overlayType={isMobile ? mobileOverlayType : overlayType}
            className={STYLES.overlayWrapper}
            foregroundContent={children}
          >
            {/* BpkOverlay requires a child component */}
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            <></>
          </BpkOverlay>
        </>
      ) : (
        <div
          className={STYLES.skeletonContainer}
          style={fallbackBackgroundColor ? { backgroundColor: fallbackBackgroundColor } : undefined}
        >
          <BpkSkeleton
            type={SKELETON_TYPES.image}
            size={{ width: '100%', height: '100%' }}
            style={IMAGE_SKELETON_STYLE.rounded}
          />
        </div>
      )}
    </div>
  );
};

export { SponsoredHeroBanner };
export type { SponsoredHeroBannerProps };
